import { action, computed } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import Select from 'react-select';
import { OptionsType, ValueType, ActionMeta } from 'react-select/src/types';
import { FormFeedback } from 'reactstrap';
import { isArray, isNotArray } from '../Utils/Array';
import { bind } from '../Utils/Bind';
import { componentAutoReaction, mobxEqual } from '../Utils/MobxTools';
import { IFormField } from '../Utils/PrimitiveFormField';

interface IOptionType<T> {
    label: string;
    value: T;
}
type OptionValueType<T> = T | IOptionType<T>;

export interface ISelectInputProps<T> {
    onChange?(): void;
    readonly field: IFormField<ValueType<T, boolean>>;
    readonly options?: OptionsType<OptionValueType<T>>;
    readonly validation?: string;
    readonly isClearable?: boolean;
    readonly className?: string;
    readonly disabled?: boolean;
    readonly required?: boolean;
    readonly id?: string;
    readonly placeholder?: string;
    readonly isMulti?: boolean;
}

@observer
export class SelectInput<T> extends React.Component<ISelectInputProps<T>> {
    public constructor(props: ISelectInputProps<T>) {
        super(props);

        componentAutoReaction(
            this,
            () => this.validations,
            v => props.field.onMount(v)
        );
    }

    public render() {
        return (
            <span>
                <Select {...this.selectProps} />
                {this.props.field.submitted &&
                    this.props.field.errors?.map((e, idx) => (
                        <FormFeedback key={idx} valid={this.validState}>
                            {e}
                        </FormFeedback>
                    ))}
            </span>
        );
    }

    @bind
    @action
    private onFieldChange(value: ValueType<OptionValueType<T>, boolean>, actionMeta: ActionMeta<OptionValueType<T>>) {
        const fieldValue: ValueType<T, boolean> = getValueFromOption(value);
        this.props.field.onChange(fieldValue);

        if (this.props.onChange) this.props.onChange();
    }

    @computed
    private get validations() {
        const rules = new Set<string>(this.props.validation?.split('|'));
        if (this.props.required) rules.add('required');
        if (this.props.required && this.props.isMulti) rules.add('required:min:1');

        return Array.from(rules).join('|');
    }

    @computed
    private get selectProps() {
        if (this.props.field.value && isArray(this.props.field.value) && !this.props.isMulti)
            throw new Error('isMulti should be set if field is an array');

        if (this.props.field.value && isNotArray(this.props.field.value) && this.props.isMulti)
            throw new Error('isMulti should be unset if field is not an array');

        const value = getOptionFromValue(this.props.field.value, this.props.options);
        const props = {
            className: this.props.className + ' react-select-container' + this.invalidClassName,
            classNamePrefix: 'react-select',
            disabled: this.props.disabled,
            id: this.props.id,
            placeholder: this.props.placeholder,
            state: this.props.field.state,
            onChange: this.onFieldChange,
            value,
            options: this.props.options,
            isMulti: this.props.isMulti,
            isClearable: this.props.isClearable,
        };

        return props;
    }

    @computed
    private get validState() {
        switch (this.props.field.state) {
            default:
                return undefined;
            case 'danger':
                return false;
            case 'success':
                return true;
        }
    }

    @computed
    private get invalidClassName() {
        switch (this.props.field.state) {
            default:
                return '';
            case 'danger':
                return ' is-invalid';
        }
    }
}

export function getValueFromOption<T>(value: ValueType<OptionValueType<T>, boolean>): ValueType<T, boolean> {
    if (!value) return null;
    else if (isArray(value)) return value.map(o => (isOptionType(o) ? o.value : o));
    else if (isNotArray(value)) return isOptionType(value) ? value.value : value;
    else throw new Error('This error should not happen');
}

export function getOptionFromValue<T>(value: ValueType<T, boolean>, options?: OptionsType<OptionValueType<T>>) {
    if (!value) return undefined;
    else if (!options) return value;
    else if (isArray(value)) return value.map(v => options.find(o => isOptionForValue(v, o)) || v);
    else if (isNotArray(value)) return options.find(o => isOptionForValue(value, o)) || value;
}

function isOptionForValue<T>(value: T, option: OptionValueType<T>) {
    return isOptionType(option) ? mobxEqual(option.value, value) : mobxEqual(option, value);
}

function isOptionType<T>(value: OptionValueType<T>): value is IOptionType<T> {
    const optionValue = value as IOptionType<T>;
    return optionValue.label != undefined;
}
